import { MARINE_REGIONS, HOME_REGION, regionById } from './marineRegions';
import { planTabs, regionBoxes, regionZoneIds, type Viewport } from './lib/regionTabs';
import {
  BAY_ZONES,
  coastalForecast,
  isBayZone,
  marineAlerts,
  MARINE_ZONES,
  zoneLabel,
  type MarineAlert,
} from './sources/marine';
import type {
  CoastalForecast,
  ForecastPeriod,
  ForecastSupplement,
} from './lib/cwfParse';
import type { ZoneTone } from './map';

const when = new Intl.DateTimeFormat([], {
  weekday: 'short', hour: 'numeric', minute: '2-digit',
});

/** Warnings outrank advisories; both outrank a quiet forecast. */
function severityClass(a: MarineAlert): string {
  if (a.severity === 'Extreme' || a.severity === 'Severe') return 'alert-severe';
  if (a.severity === 'Moderate') return 'alert-warning';
  return 'alert-minor';
}

/**
 * Compact by default. Four active alerts at full length pushed the Bay's own
 * forecast off the bottom of the panel, so the detail folds away behind a
 * disclosure and the headline facts stay one line each.
 */
function alertCard(a: MarineAlert): string {
  const window = a.ends
    ? `${a.onset ? when.format(a.onset) : 'now'} → ${when.format(a.ends)}`
    : '';
  return `
    <article class="alert-card ${severityClass(a)}" role="button" tabindex="0"
             aria-pressed="false" data-zones="${a.zones.join(',')}" data-tone="alert"
             data-zone-key="alert:${a.id}">
      <header>
        <span class="alert-icon" aria-hidden="true">▲</span>
        <h3>${a.event}</h3>
        <span class="zone-pin" aria-hidden="true">On map</span>
      </header>
      <p class="alert-area">${a.areaDesc}</p>
      ${window ? `<p class="alert-window">${window}</p>` : ''}
      <details class="alert-detail">
        <summary>Details</summary>
        <p class="alert-body">${a.description}</p>
      </details>
    </article>`;
}

/** How many periods stand above the fold; the product carries about eleven. */
const LEAD_PERIODS = 3;

function periodRows(periods: ForecastPeriod[]): string {
  return periods.map((p) => `<div><dt>${p.name}</dt><dd>${p.text}</dd></div>`).join('');
}

/**
 * A supplement such as the San Francisco Bar forecast.
 *
 * Given its own slot directly under the Bay rather than left inside its parent
 * zone. It belongs to PZZ545, which lives behind the "Outside the Bay"
 * disclosure — but the bar sits in the mouth of the Gate, and what it says,
 * that swell can more than double across it and break on a strong ebb, is the
 * most consequential paragraph in the product for anyone leaving this Bay.
 * Attributed to its zone so the move does not misrepresent where it came from.
 */
function supplementSection(s: ForecastSupplement, from: string): string {
  return `
    <section class="supplement">
      <h4>${titleCase(s.title)}</h4>
      <p class="supplement-from">Issued with ${from}</p>
      <dl class="period-list">${periodRows(s.sections)}</dl>
    </section>`;
}

/** NWS ships headings in caps; sentence case reads better beside our own text. */
function titleCase(s: string): string {
  return s.replace(/\b([A-Z])([A-Z']*)\b/g, (_, a, b) => a + b.toLowerCase());
}

function zoneSection(forecast: CoastalForecast, zoneId: string): string {
  const zone = forecast.zones.find((z) => z.zones.includes(zoneId));
  if (!zone) return '';
  const short = zoneLabel(zoneId, zone.name);

  const lead = zone.periods.slice(0, LEAD_PERIODS);
  const rest = zone.periods.slice(LEAD_PERIODS);

  // A headline names a warning that also arrives as an alert. Carrying the zone
  // lets the click find the matching card instead of repeating its text here.
  const headlines = zone.headlines
    .map(
      (h) =>
        `<button type="button" class="zone-headline" data-headline-zone="${zoneId}"
                 data-headline="${h.replace(/"/g, '&quot;')}">${h}</button>`,
    )
    .join('');

  // `zoneId`, not `zone.zones`: one product header can cover several zones that
  // share wording (PZZ545-560-), but this section is titled as one of them, so
  // lighting up both would highlight water the heading never claimed.
  return `
    <section class="zone-forecast" role="button" tabindex="0" aria-pressed="false"
             data-zones="${zoneId}" data-tone="plain" data-zone-key="zone:${zoneId}"
             aria-label="${short}. Press to show this area on the map.">
      <header class="zone-head">
        <h3>${short}</h3>
        <span class="zone-pin" aria-hidden="true">On map</span>
      </header>
      <p class="zone-full">${zone.name}</p>
      ${headlines}
      <dl class="period-list">${periodRows(lead)}</dl>
      ${
        rest.length
          ? `<details class="more-periods">
               <summary>${rest.length} more periods</summary>
               <dl class="period-list">${periodRows(rest)}</dl>
             </details>`
          : ''
      }
    </section>`;
}

export interface ZoneSelection {
  zones: string[];
  tone: ZoneTone;
}

/** Reflects a card's on/off state to both the eye and assistive tech. */
function setPressed(card: HTMLElement, on: boolean): void {
  card.classList.toggle('is-shown', on);
  card.setAttribute('aria-pressed', String(on));
}

/**
 * Marine forecast, active warnings, and a route to the Local Notice to Mariners.
 *
 * Everything here is a forecast or a notice, never an observation, and the panel
 * says so — the rest of the app is measured data and the difference matters.
 */
export interface MarinePopupHooks {
  /** The map's current bounds, for deciding which regions to offer as tabs. */
  getView: () => Viewport;
  /**
   * Outline a region's waters while the pointer is on its tab; null restores
   * whatever the reader had actually selected.
   */
  onRegionPreview: (zoneIds: string[] | null) => void;
  /** Frame a region's waters on the map, on choosing it. */
  onRegionFocus: (boxes: Array<[number, number, number, number]>) => void;
  /**
   * Hands back a redraw for the region strip, to be called whenever the map
   * settles. The strip is meant to offer the regions you are looking at, which
   * it cannot do if it is only built once.
   */
  onReady: (redrawRegions: () => void) => void;
  /** Hands back a way to jump to the card speaking for a zone clicked on the map. */
  onZoneFocus: (focus: (zoneId: string) => void) => void;
}

/**
 * "Boston / Norton, MA" -> "Boston", "Miami - South Florida" -> "Miami".
 *
 * NWS names carry a state and often a second city, which wrapped the strip onto
 * a second line. The first place named is the one people recognise.
 */
function shortRegionName(name: string): string {
  return name.split(/[/,]| - /)[0].trim();
}

export function buildMarinePopup(
  onClose: () => void,
  /** `grew` is true when a card was just added, which is when framing is wanted. */
  onZones: (selections: ZoneSelection[], grew: boolean) => void,
  hooks: MarinePopupHooks,
): HTMLElement {
  const root = document.createElement('div');
  root.className = 'popup';

  root.innerHTML = `
    <header class="popup-head">
      <button type="button" class="popup-close" aria-label="Close marine forecast">×</button>
      <h2>Marine forecast</h2>
      <p class="popup-sub">
        <span class="muted">forecast, not observed</span>
      </p>
      <nav class="region-tabs" aria-label="Forecast region"></nav>
    </header>
    <div class="bay-slot"><p class="chart-empty">Loading forecast…</p></div>
    <div class="supplement-slot"></div>
    <div class="alerts-slot"></div>
    <div class="synopsis-slot"></div>
    <details class="other-zones">
      <summary class="zones-summary">Outside the Bay</summary>
      <div class="zones-slot"></div>
    </details>
    <a class="source-link" target="_blank" rel="noopener">NWS marine page ↗</a>`;

  root.querySelector<HTMLButtonElement>('.popup-close')!.addEventListener('click', onClose);

  /** Which region's forecast is showing. The Bay unless the reader says otherwise. */
  let region = HOME_REGION;
  const tabsHost = root.querySelector<HTMLElement>('.region-tabs')!;

  /**
   * Draws the region strip.
   *
   * Rebuilt on every region change rather than patched: the set of tabs depends
   * on where the map is, so which ones exist is not stable between renders.
   */
  const renderTabs = (): void => {
    const { tabs, rest } = planTabs(MARINE_REGIONS, hooks.getView(), region);

    /*
     * The tabs scroll; "More" does not.
     *
     * Along the Gulf sixteen regions are in frame, and with everything in one
     * scrolling box the "More" control fell below the fold — stranding the
     * twenty-six regions that were not on screen, which is exactly what it is
     * for.
     */
    tabsHost.innerHTML =
      '<div class="region-tab-list">' +
      tabs
        .map(
          (r) =>
            `<button type="button" class="region-tab${r.id === region ? ' is-current' : ''}"
               data-region="${r.id}"${r.id === region ? ' aria-current="true"' : ''}
             >${shortRegionName(r.name)}</button>`,
        )
        .join('') +
      '</div>' +
      (rest.length
        ? `<details class="region-more">
             <summary aria-label="More forecast regions">More</summary>
             <div class="region-list">${rest
               .map(
                 (r) =>
                   `<button type="button" class="region-choice" data-region="${r.id}">${r.name}</button>`,
               )
               .join('')}</div>
           </details>`
        : '');

    // Hover outlines the region's waters; leaving restores the real selection.
    tabsHost.querySelectorAll<HTMLElement>('[data-region]').forEach((el) => {
      const target = regionById(el.dataset.region!);
      if (!target) return;

      el.addEventListener('pointerenter', () => hooks.onRegionPreview(regionZoneIds(target)));
      el.addEventListener('focus', () => hooks.onRegionPreview(regionZoneIds(target)));
      el.addEventListener('pointerleave', () => hooks.onRegionPreview(null));
      el.addEventListener('blur', () => hooks.onRegionPreview(null));

      el.addEventListener('click', () => {
        hooks.onRegionPreview(null);
        // Take the map there whether or not the forecast changes: clicking the
        // region you are already reading is a reasonable way to say "show me
        // where this is".
        hooks.onRegionFocus(regionBoxes(target));
        if (target.id === region) return;
        region = target.id;
        // Selecting a region drops the zone cards: they belong to the forecast
        // that is leaving, and leaving them lit would paint water the panel no
        // longer mentions.
        selected.clear();
        onZones([], false);
        renderTabs();
        load();
      });
    });
  };


  const baySlot = root.querySelector<HTMLElement>('.bay-slot')!;
  const alertsSlot = root.querySelector<HTMLElement>('.alerts-slot')!;
  const synopsisSlot = root.querySelector<HTMLElement>('.synopsis-slot')!;
  const zonesSlot = root.querySelector<HTMLElement>('.zones-slot')!;
  const supplementSlot = root.querySelector<HTMLElement>('.supplement-slot')!;
  const zonesSummary = root.querySelector<HTMLElement>('.zones-summary')!;

  /**
   * Which cards are currently drawn on the map, keyed by `data-zone-key`.
   *
   * Keyed by card rather than by zone because an alert can span several zones
   * and a forecast section names one, so the same zone can be reachable from
   * two different cards. The key keeps each card's own on/off state honest.
   */
  const selected = new Map<string, ZoneSelection>();

  /** The card the pointer is over, previewed on the map without committing. */
  let hovered: { key: string; selection: ZoneSelection } | null = null;

  /** Everything to draw: what is pinned, plus whatever is merely being hovered. */
  const compose = (): ZoneSelection[] => {
    const out = [...selected.values()];
    if (hovered && !selected.has(hovered.key)) out.push(hovered.selection);
    return out;
  };

  const publish = (grew: boolean) => {
    onZones(compose(), grew);
  };

  /**
   * Toggles a card, whole-card click being the affordance.
   *
   * Clicks that land on something already interactive — the Details
   * disclosure, a headline that jumps to its alert, a link — are left alone,
   * or opening the details would also light up the map.
   */
  /**
   * One button for every alert at once, and the same button to take them off.
   *
   * Published as a single update rather than a toggle per card, so the map is
   * repainted once and framed once instead of thirty-two times.
   */
  const wireShowAll = (host: HTMLElement): void => {
    const button = host.querySelector<HTMLButtonElement>('.alerts-all');
    if (!button) return;

    const cards = [...host.querySelectorAll<HTMLElement>('[data-zone-key]')];
    const allLit = () => cards.length > 0 && cards.every((c) => selected.has(c.dataset.zoneKey!));

    const paintButton = () => {
      button.textContent = allLit() ? 'Clear all from map' : `Show all ${cards.length} on map`;
    };
    paintButton();

    button.addEventListener('click', () => {
      const turningOff = allLit();
      for (const card of cards) {
        const key = card.dataset.zoneKey!;
        const zones = (card.dataset.zones ?? '').split(',').filter(Boolean);
        if (turningOff) {
          selected.delete(key);
        } else if (zones.length) {
          selected.set(key, { zones, tone: card.dataset.tone === 'alert' ? 'alert' : 'plain' });
        }
        setPressed(card, !turningOff && zones.length > 0);
      }
      hovered = null;
      paintButton();
      publish(!turningOff);
    });
  };

  const wireToggle = (host: HTMLElement) => {
    host.querySelectorAll<HTMLElement>('[data-zone-key]').forEach((card) => {
      const key = card.dataset.zoneKey!;
      // Re-rendered slots lose their state, so reflect what is already lit.
      setPressed(card, selected.has(key));

      const selectionOf = (): ZoneSelection | null => {
        const zones = (card.dataset.zones ?? '').split(',').filter(Boolean);
        if (!zones.length) return null;
        return { zones, tone: card.dataset.tone === 'alert' ? 'alert' : 'plain' };
      };

      const toggle = () => {
        if (selected.has(key)) {
          selected.delete(key);
          setPressed(card, false);
          // Drop the preview too, so switching a card off actually clears its
          // water instead of leaving it drawn until the pointer moves away.
          if (hovered?.key === key) hovered = null;
          publish(false);
          return;
        }
        const selection = selectionOf();
        if (!selection) return;
        selected.set(key, selection);
        setPressed(card, true);
        publish(true);
      };

      card.addEventListener('click', (e) => {
        const target = e.target as Element | null;
        // `closest` walks past the card as well as inside it, and the offshore
        // sections sit in a <details>, so an unscoped match caught every one of
        // them on the wrapper. Only a control *within* the card blocks the click.
        const blocker = target?.closest('button, a, summary, details, input');
        if (blocker && card.contains(blocker)) return;
        toggle();
      });

      // Hover previews the area; only a click makes it stick. Mouse only —
      // a touch fires enter immediately before click and would preview
      // everything the finger passes over.
      card.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || selected.has(key)) return;
        const selection = selectionOf();
        if (!selection) return;
        hovered = { key, selection };
        publish(false);
      });

      card.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse' || hovered?.key !== key) return;
        hovered = null;
        publish(false);
      });

      // role="button" does not bring the keyboard behaviour with it.
      card.addEventListener('keydown', (e) => {
        if (e.target !== card) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        toggle();
      });
    });
  };

  /**
   * The alert card a forecast headline is announcing, if it is on screen.
   *
   * A headline reads "GALE WARNING IN EFFECT THROUGH LATE TONIGHT" while the
   * card is titled "Gale Warning", so the event name is matched as a prefix
   * against the headline. Zone has to match too: two zones can both be under a
   * gale and each headline should reach its own card.
   */
  const cardForHeadline = (zone: string, headline: string): HTMLElement | null => {
    const cards = alertsSlot.querySelectorAll<HTMLElement>('.alert-card');
    for (const card of cards) {
      const zones = (card.dataset.zones ?? '').split(',');
      if (!zones.includes(zone)) continue;
      const event = card.querySelector('h3')?.textContent?.trim().toUpperCase() ?? '';
      if (event && headline.toUpperCase().startsWith(event)) return card;
    }
    return null;
  };

  /**
   * Links headlines to their alert cards.
   *
   * Re-run whenever either half lands, because the forecast and the alert list
   * are independent requests and either can arrive first. A headline with no
   * matching card stays inert rather than offering a click that goes nowhere.
   */
  /** The card the map last pointed at, so clicking its water again folds it up. */
  let lastFocused: HTMLElement | null = null;

  /** Scrolls a card into view and flashes it, restarting the flash each time. */
  const flagCard = (card: HTMLElement): void => {
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.classList.remove('is-flagged');
    void card.offsetWidth;
    card.classList.add('is-flagged');
  };

  /**
   * The way back from the map.
   *
   * Clicking a card lights its water; this finds the card for a zone clicked on
   * the map. Alerts win over forecast sections when both speak for a zone —
   * a warning is the thing you clicked the water to read.
   */
  const focusZone = (zoneId: string): void => {
    const cards = [...root.querySelectorAll<HTMLElement>('[data-zone-key]')].filter((c) =>
      (c.dataset.zones ?? '').split(',').includes(zoneId),
    );
    const card = cards.find((c) => c.dataset.tone === 'alert') ?? cards[0];
    if (!card) return;

    // A zone under an unopened disclosure would otherwise scroll to nothing.
    const wrapper = card.closest('details');
    if (wrapper && !wrapper.open) wrapper.open = true;

    /*
     * Open what the card has to say, since reading it is the reason for
     * clicking its water. Clicking the same water again folds it back, which
     * makes the map a way in and out rather than only in.
     */
    const detail = card.querySelector<HTMLDetailsElement>('.alert-detail, .more-periods');
    if (detail) detail.open = lastFocused === card ? !detail.open : true;
    lastFocused = card;

    flagCard(card);
  };

  const linkHeadlines = () => {
    root.querySelectorAll<HTMLButtonElement>('.zone-headline').forEach((el) => {
      const zone = el.dataset.headlineZone ?? '';
      const headline = el.dataset.headline ?? '';
      const target = cardForHeadline(zone, headline);

      el.classList.toggle('is-linked', target !== null);
      el.disabled = target === null;
      if (!target || el.dataset.wired) return;

      el.dataset.wired = '1';
      el.addEventListener('click', () => {
        const card = cardForHeadline(zone, headline);
        if (card) flagCard(card);
      });
    });
  };

  /** The zones of the region on screen — alerts must match the forecast shown. */
  const zoneIds = (): string[] => regionById(region)?.zones.map(([id]) => id) ?? [];

  /**
   * Loads the showing region's forecast and alerts.
   *
   * The Bay keeps its own shape: its two zones lead and everything else sits
   * under "Outside the Bay". No other region has a home water to lead with, so
   * for those the zones are simply listed in the order the product prints them.
   */
  const load = (): void => {
    // The cards are about to be replaced, so the remembered one is gone.
    lastFocused = null;
    const home = region === HOME_REGION;
    baySlot.hidden = !home;
    baySlot.innerHTML = home ? '<p class="chart-empty">Loading forecast…</p>' : '';
    alertsSlot.innerHTML = '';
    synopsisSlot.innerHTML = '';
    supplementSlot.innerHTML = '';
    zonesSlot.innerHTML = '<p class="chart-empty">Loading forecast…</p>';
    zonesSummary.textContent = home ? 'Outside the Bay' : 'All zones';
    root.querySelector<HTMLAnchorElement>('.source-link')!.href =
      `https://www.weather.gov/${region.toLowerCase()}/marine`;

  marineAlerts(zoneIds())
      .then((alerts) => {
        if (alerts.length === 0) {
          alertsSlot.innerHTML =
            '<p class="alerts-clear">No active warnings or advisories.</p>';
          return;
        }
  
        // An alert over the Bay itself outranks one 40 miles offshore, whatever
        // its severity — this is a Bay site.
        const bay = alerts.filter((a) => isBayZone(a.zones));
        const offshore = alerts.filter((a) => !isBayZone(a.zones));
  
        /*
         * With thirty-two advisories over one region, lighting them up one card
         * at a time is not a reasonable ask — Dixon Entrance has exactly that.
         */
        const allBar =
          alerts.length > 1
            ? `<div class="alerts-bar">
                 <button type="button" class="alerts-all">Show all ${alerts.length} on map</button>
               </div>`
            : '';

        alertsSlot.innerHTML =
          allBar +
          (bay.length
            ? `<h3 class="alerts-title">In the Bay</h3>${bay.map(alertCard).join('')}`
            : '') +
          (offshore.length
            ? `<h3 class="alerts-title">Offshore · ${offshore.length}</h3>` +
              offshore.map(alertCard).join('')
            : '');
  
        wireToggle(alertsSlot);
        wireShowAll(alertsSlot);
        linkHeadlines();
      })
      .catch(() => {
        alertsSlot.innerHTML = '<p class="alerts-clear">Could not load alerts.</p>';
      });
  
    coastalForecast(region)
      .then(({ forecast, issued }) => {
        // The two Bay zones lead: they are what this site is about, and burying
        // them under four offshore alerts put them below the fold.
        const bayHtml = BAY_ZONES.map((id) => zoneSection(forecast, id)).join('');
        baySlot.innerHTML = bayHtml || '<p class="chart-empty">No Bay forecast available.</p>';
        wireToggle(baySlot);
  
        synopsisSlot.innerHTML = forecast.synopsis
          ? `<section class="synopsis"><h3>Synopsis</h3><p>${forecast.synopsis}</p>
             <p class="muted issued">Issued ${when.format(issued)}</p></section>`
          : '';
  
        // Bar and Fourfathom Bank: the water at the Gate, wherever NWS filed it.
        const extras = forecast.zones.flatMap((z) =>
          z.supplements.map((s) => supplementSection(s, zoneLabel(z.zones[0], z.name))),
        );
        supplementSlot.innerHTML = extras.join('');
  
        // Every zone the product carries, in our order, then anything NWS added
        // that this registry has not caught up with — a new zone should appear
        // under its own name rather than vanish.
        // Only the Bay has a curated order; other regions are listed as the
        // product prints them, which is already coast-wise.
        const known: string[] = home ? MARINE_ZONES.map((z) => z.id) : [];
        const unknown = forecast.zones
          .flatMap((z) => z.zones)
          .filter((id) => !known.includes(id));
        const rest = [...known, ...new Set(unknown)]
          .filter((id) => !BAY_ZONES.includes(id))
          .map((id) => zoneSection(forecast, id))
          .filter(Boolean);
  
        zonesSlot.innerHTML =
          rest.join('') || '<p class="chart-empty">No other zones available.</p>';
        // "Outside the Bay" only means anything in the Bay's own forecast.
        if (rest.length) {
          zonesSummary.textContent = home
            ? `Outside the Bay · ${rest.length} zones`
            : `All zones · ${rest.length}`;
        }
        wireToggle(zonesSlot);
        linkHeadlines();
      })
      .catch((err) => {
        baySlot.innerHTML = `<p class="chart-empty">Could not load forecast: ${
          err instanceof Error ? err.message : String(err)
        }</p>`;
      });
  };

  renderTabs();
  load();

  // Redraw once attached: the strip subtracts the panel's own width from the
  // map, and an unattached panel measures zero.
  root.addEventListener('mount', () => renderTabs());

  /*
   * Follow the map.
   *
   * Built once, the strip described wherever the map happened to be when the
   * panel opened — and worse, choosing a region rebuilt it against the view the
   * map was still animating away from, so picking Newport from a California
   * view offered Los Angeles and Eureka. Redrawing when the map settles means
   * the strip always describes the water on screen.
   */
  hooks.onZoneFocus(focusZone);
  // Warm the outline chunk while the reader is reading, so the first hover
  // paints rather than waits.
  hooks.onRegionPreview([]);

  hooks.onReady(() => {
    // Leave the open list alone: rebuilding it under the pointer would close it
    // mid-choice.
    if (root.querySelector<HTMLDetailsElement>('.region-more')?.open) return;
    renderTabs();
  });

  return root;
}
